import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { featureLabel } from '../components/EntitlementStates';

// Platform-admin dashboard — the money-failsafe control panel.
//   • Global spend circuit-breaker: live day/month totals vs ceilings, pause/resume.
//   • Over-limit requests: approve (raise the org's cap for the period) or deny.
//   • Tunable config: per-call cost estimates, global ceilings, per-tier entitlement caps.
// Backend gates every /api/admin route with requirePlatformAdmin; this page is also nav-gated.

const api = (path, opts = {}) =>
  fetch(`/api/admin${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || 'Request failed'); return d; });

const card = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 20, marginBottom: 20 };
const h2 = { fontSize: 16, fontWeight: 700, color: 'var(--white)', marginBottom: 14 };
const btn = (bg) => ({ padding: '6px 12px', borderRadius: 7, border: 'none', background: bg, color: '#fff', fontSize: 13, cursor: 'pointer' });
const money = (n) => '$' + Number(n || 0).toFixed(2);

export default function Admin() {
  const { user } = useAuth();
  const [spend, setSpend] = useState(null);
  const [requests, setRequests] = useState([]);
  const [config, setConfig] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgDetail, setOrgDetail] = useState(null);   // { org, entitlements, spend }
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [s, r, c, e, o] = await Promise.all([
        api('/spend'), api('/over-limit'), api('/config'), api('/entitlements'), api('/orgs'),
      ]);
      setSpend(s); setRequests(r); setConfig(c); setEntitlements(e); setOrgs(o);
    } catch (e) { setErr(e.message); }
  }, []);

  // Open an account's detail (entitlements + usage + spend).
  const openOrg = async (id) => { try { setOrgDetail(await api('/orgs/' + id)); } catch (e) { setErr(e.message); } };
  // Set/clear a per-account tool override, then refresh the detail + the list badges.
  const setTool = async (id, feature, body) => {
    setBusy(true);
    try {
      const d = await api('/orgs/' + id + '/entitlement', { method: 'POST', body: JSON.stringify({ feature_key: feature, ...body }) });
      setOrgDetail(od => (od && od.org.id === id ? { ...od, entitlements: d.entitlements } : od));
      setOrgs(await api('/orgs'));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };

  if (!user?.is_platform_admin) return <div style={{ color: 'var(--gray-300)' }}>Platform admin only.</div>;

  const globalBreaker = spend?.breakers?.find(b => b.category === 'global');
  const paused = globalBreaker?.state === 'paused';
  const dayPct = spend?.ceilings?.daily ? Math.min(100, (spend.spend.day / spend.ceilings.daily) * 100) : 0;
  const monthPct = spend?.ceilings?.monthly ? Math.min(100, (spend.spend.month / spend.ceilings.monthly) * 100) : 0;

  return (
    <div style={{ maxWidth: 920 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--white)', marginBottom: 6 }}>Admin &amp; Spend</h1>
      <p style={{ color: 'var(--gray-500)', fontSize: 13, marginBottom: 20 }}>Platform money-failsafe controls. Thresholds are data — edits take effect immediately, no deploy.</p>
      {err && <div style={{ ...card, borderColor: 'var(--red)', color: 'var(--red)' }}>{err}</div>}

      {/* ── Accounts: per-account tool toggles + spend ──────────────────── */}
      <div style={card}>
        <div style={h2}>Accounts</div>
        <input placeholder="Search name or email…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, marginBottom: 12 }} />
        <table style={{ width: '100%', fontSize: 13, color: 'var(--gray-300)', borderCollapse: 'collapse' }}>
          <thead><tr style={{ color: 'var(--gray-500)', textAlign: 'left' }}><th>Account</th><th>Owner</th><th>Tier</th><th></th></tr></thead>
          <tbody>
            {orgs.filter(o => { const q = orgSearch.toLowerCase(); return !q || (o.name || '').toLowerCase().includes(q) || (o.owner_email || '').toLowerCase().includes(q); }).map(o => (
              <tr key={o.id} style={{ borderTop: '1px solid var(--navy-border)' }}>
                <td style={{ padding: '7px 0', color: 'var(--white)', fontWeight: 600 }}>{o.name} <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>#{o.id}</span></td>
                <td>{o.owner_email || '—'}</td>
                <td><span style={{ textTransform: 'capitalize' }}>{o.plan}</span>{o.override_count > 0 && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 8, background: 'var(--teal-dim)', color: 'var(--teal)', fontWeight: 700 }}>{o.override_count} override{o.override_count > 1 ? 's' : ''}</span>}</td>
                <td style={{ textAlign: 'right' }}><button style={btn('var(--teal)')} onClick={() => openOrg(o.id)}>Manage</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orgDetail && <AccountDetail detail={orgDetail} onClose={() => setOrgDetail(null)} onSet={setTool} busy={busy} />}

      {/* ── Spend circuit-breaker ─────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={h2}>Global Spend Circuit-Breaker</div>
          <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: paused ? 'rgba(220,60,60,0.15)' : 'rgba(40,160,110,0.15)', color: paused ? 'var(--red)' : 'var(--teal)' }}>
            {paused ? 'PAUSED' : 'OK'}{spend?.enabled === false ? ' (breaker disabled)' : ''}
          </span>
        </div>
        {spend && (
          <div style={{ display: 'flex', gap: 24, marginTop: 14, flexWrap: 'wrap' }}>
            <Meter label="Today" used={spend.spend.day} ceil={spend.ceilings.daily} pct={dayPct} />
            <Meter label="This month" used={spend.spend.month} ceil={spend.ceilings.monthly} pct={monthPct} />
          </div>
        )}
        {paused && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--gray-300)' }}>
            Reason: {globalBreaker?.reason || '—'}
          </div>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
          {paused
            ? <button disabled={busy} style={btn('var(--teal)')} onClick={() => act(() => api('/spend/resume', { method: 'POST', body: JSON.stringify({ scope: 'global' }) }))}>Resume external calls</button>
            : <button disabled={busy} style={btn('var(--red)')} onClick={() => act(() => api('/spend/pause', { method: 'POST', body: JSON.stringify({ scope: 'global', reason: 'Manual pause from admin' }) }))}>Pause all external calls</button>}
        </div>
        {spend?.byCategoryToday?.length > 0 && (
          <table style={{ width: '100%', marginTop: 16, fontSize: 13, color: 'var(--gray-300)', borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: 'var(--gray-500)', textAlign: 'left' }}><th>Category (today)</th><th>Calls</th><th>Est. cost</th></tr></thead>
            <tbody>{spend.byCategoryToday.map(c => (
              <tr key={c.category} style={{ borderTop: '1px solid var(--navy-border)' }}><td>{c.category}</td><td>{c.calls}</td><td>{money(c.cost)}</td></tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {/* ── Over-limit requests ───────────────────────────────────────── */}
      <div style={card}>
        <div style={h2}>Over-Limit Requests</div>
        {requests.length === 0 && <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>No requests.</div>}
        {requests.map(r => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid var(--navy-border)', fontSize: 13 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--white)', fontWeight: 600 }}>{r.org_name} <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>(#{r.org_id}, {r.plan_key})</span></div>
              <div style={{ color: 'var(--gray-300)' }}>{r.feature_key} · {r.period} · cap {r.plan_limit}, used {r.used_at_request}{r.granted_limit ? ` → granted ${r.granted_limit}` : ''}</div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: r.status === 'pending' ? 'var(--gold)' : r.status === 'approved' ? 'var(--teal)' : 'var(--gray-500)' }}>{r.status}</span>
            {r.status === 'pending' && (
              <>
                <button disabled={busy} style={btn('var(--teal)')} onClick={() => act(() => api(`/over-limit/${r.id}/approve`, { method: 'POST', body: '{}' }))}>Approve</button>
                <button disabled={busy} style={btn('var(--red)')} onClick={() => act(() => api(`/over-limit/${r.id}/deny`, { method: 'POST', body: '{}' }))}>Deny</button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* ── Tunable config ────────────────────────────────────────────── */}
      <div style={card}>
        <div style={h2}>Per-Call Cost Estimates ($)</div>
        {config?.cost_config?.map(c => (
          <ConfigRow key={c.category} label={c.category} value={c.est_cost_usd}
            onSave={(v) => act(() => api('/config/cost', { method: 'POST', body: JSON.stringify({ category: c.category, est_cost_usd: parseFloat(v) }) }))} />
        ))}
      </div>

      <div style={card}>
        <div style={h2}>Global Settings</div>
        {config?.platform_config?.map(c => (
          <ConfigRow key={c.key} label={c.key} value={c.value}
            onSave={(v) => act(() => api('/config/platform', { method: 'POST', body: JSON.stringify({ key: c.key, value: v }) }))} />
        ))}
      </div>

      <div style={card}>
        <div style={h2}>Per-Tier Caps <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 12 }}>(blank = unlimited, 0 = locked)</span></div>
        <table style={{ width: '100%', fontSize: 13, color: 'var(--gray-300)', borderCollapse: 'collapse' }}>
          <thead><tr style={{ color: 'var(--gray-500)', textAlign: 'left' }}><th>Plan</th><th>Feature</th><th>Limit</th></tr></thead>
          <tbody>{entitlements.map((e, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--navy-border)' }}>
              <td>{e.plan}</td><td>{e.feature_key}</td>
              <td><InlineNum value={e.limit_value}
                onSave={(v) => act(() => api('/entitlements', { method: 'POST', body: JSON.stringify({ plan: e.plan, feature_key: e.feature_key, limit_value: v === '' ? null : parseInt(v) }) }))} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Meter({ label, used, ceil, pct }) {
  const hot = pct >= 80;
  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--gray-300)', marginBottom: 6 }}>
        <span>{label}</span><span>{money(used)} / {ceil != null ? money(ceil) : '∞'}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: 'var(--navy-border)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: hot ? 'var(--red)' : 'var(--teal)' }} />
      </div>
    </div>
  );
}

function ConfigRow({ label, value, onSave }) {
  const [v, setV] = useState(String(value ?? ''));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid var(--navy-border)' }}>
      <span style={{ flex: 1, fontSize: 13, color: 'var(--gray-300)' }}>{label}</span>
      <input value={v} onChange={e => setV(e.target.value)} style={{ width: 160, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13 }} />
      <button style={btn('var(--teal)')} onClick={() => onSave(v)}>Save</button>
    </div>
  );
}

function InlineNum({ value, onSave }) {
  const [v, setV] = useState(value == null ? '' : String(value));
  return (
    <span style={{ display: 'flex', gap: 6 }}>
      <input value={v} placeholder="∞" onChange={e => setV(e.target.value)} style={{ width: 70, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13 }} />
      <button style={{ ...btn('var(--navy-border)'), padding: '3px 8px' }} onClick={() => onSave(v)}>✓</button>
    </span>
  );
}

// One account's detail: spend breakdown + per-tool On / Off / Plan toggles.
function AccountDetail({ detail, onClose, onSet, busy }) {
  const { org, entitlements, spend } = detail;
  const stateLabel = (e) => e.access === 'unlimited' ? 'On (unlimited)' : e.access === 'none' ? 'Off' : `Cap ${e.limit}`;
  const pill = (active, color) => ({
    padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer',
    border: `1px solid ${active ? color : 'var(--navy-border)'}`,
    background: active ? color : 'transparent', color: active ? '#fff' : 'var(--gray-300)',
  });
  return (
    <div style={{ ...card, borderColor: 'var(--teal)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>{org.name} <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>#{org.id}</span></div>
          <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>{org.owner_email || '—'} · <span style={{ textTransform: 'capitalize' }}>{org.plan}</span> plan</div>
        </div>
        <button style={btn('var(--navy-border)')} onClick={onClose}>Close</button>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 0 8px' }}>Spend</div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 13, color: 'var(--gray-300)' }}>
        <div>This month: <strong style={{ color: 'var(--white)' }}>{money(spend.total_period)}</strong></div>
        <div>All time: <strong style={{ color: 'var(--white)' }}>{money(spend.total_all)}</strong></div>
      </div>
      {spend.by_category_all.length === 0
        ? <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 14 }}>No metered spend recorded yet.</div>
        : <table style={{ width: '100%', fontSize: 12, color: 'var(--gray-300)', borderCollapse: 'collapse', marginBottom: 14 }}>
            <thead><tr style={{ color: 'var(--gray-500)', textAlign: 'left' }}><th>Category</th><th>Calls (all time)</th><th>This month</th><th>Cost (all time)</th></tr></thead>
            <tbody>{spend.by_category_all.map(c => {
              const per = spend.by_category_period.find(p => p.category === c.category);
              return <tr key={c.category} style={{ borderTop: '1px solid var(--navy-border)' }}><td>{c.category}</td><td>{c.calls}</td><td>{money(per?.cost || 0)}</td><td>{money(c.cost)}</td></tr>;
            })}</tbody>
          </table>}

      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '4px 0 8px' }}>Tools</div>
      {entitlements.map(e => (
        <div key={e.feature} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid var(--navy-border)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{featureLabel(e.feature)}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              {stateLabel(e)} · {e.source === 'override' ? 'per-account override' : 'plan default'}
              {e.access === 'limited' && <> · used {e.used}/{e.limit} this month</>}
            </div>
          </div>
          <button disabled={busy} onClick={() => onSet(org.id, e.feature, { limit_value: null })} style={pill(e.override === 'unlimited', 'var(--teal)')}>On</button>
          <button disabled={busy} onClick={() => onSet(org.id, e.feature, { limit_value: 0 })} style={pill(e.override === 0, 'var(--red)')}>Off</button>
          <button disabled={busy} onClick={() => onSet(org.id, e.feature, { reset: true })} style={pill(e.override === null, 'var(--gray-500)')}>Plan</button>
        </div>
      ))}
    </div>
  );
}
